import { Autocomplete, Box } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
//thunk
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { fetchCategoryByDropDown } from "slices/Masters/category/thunk";
import { addSubCategory } from "slices/Masters/sub-category/thunk";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const SubCategoryForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);

  // fetch Category By DropDown
  useEffect(() => {
    const fetchDropdown = async () => {
      try {
        const res = await dispatch(fetchCategoryByDropDown()).unwrap();
        setCategoryOptions(res || []);
      } catch {
        toast.error("Failed to load categories");
      }
    };
    fetchDropdown();
  }, [dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      categoryIds: [] as Array<{ category_id: string; category_name: string }>,
      subCategoryName: "",
    },
    validationSchema: Yup.object({
      categoryIds: Yup.array()
        .of(Yup.string())
        .min(1, "Please select at least one category")
        .required("Please select at least one category"),
      subCategoryName: Yup.string()
        .trim()
        .required("Please enter sub-category name"),
    }),
    onSubmit: async (values) => {
      const payload = {
        category_ids: values.categoryIds,
        sub_category_name: values.subCategoryName.trim(),
      };
      setIsSubmitting(true);
      try {
        await dispatch(addSubCategory(payload)).unwrap();
        clearTableState(TableKeys.MASTER.SUB_CATEGORY);
        toast.success("Sub-category added successfully");
        navigate("/master/sub-category");
      } catch (error: any) {
        console.error("Error during form submit:", error);
        toast.error(
          error?.message || "Something went wrong while saving sub-category",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount, formik.isSubmitting);

  return (
    <PageContainer>
      <PageBreadcrumb
        title={"Add Sub Category"}
        titleLink="/master/sub-category"
        breadcrumbs={[
          { label: "Sub Category", to: "/master/sub-category" },
          { label: "Add Sub Category", to: "" },
        ]}
        backNavigationPath={-1}
      />

      <hr />

      <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
        <Row className="mb-3">
          <Col xs={12} md={6} className="mb-3">
            <Form.Label className="fw-semibold">
              Category <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={categoryOptions}
              getOptionLabel={(option: any) =>
                typeof option === "string" ? option : option.category_name
              }
              value={categoryOptions.filter((c: any) =>
                formik.values.categoryIds.includes(c.category_id),
              )}
              onChange={(event, selected) =>
                formik.setFieldValue(
                  "categoryIds",
                  selected.map((s: any) => s.category_id),
                )
              }
              onBlur={() => formik.setFieldTouched("categoryIds", true)}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",

                      // ✅ Hover state
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },

                      // ✅ Selected state
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    {/* Option Label */}
                    <span>
                      {typeof option === "string"
                        ? option
                        : option.category_name}
                    </span>

                    {/* Show X only if option is selected */}
                    {selected && (
                      <span
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          color: "#d32f2f",
                          fontWeight: "bold",
                        }}
                        onClick={(e) => {
                          e.stopPropagation(); // prevent normal selection toggling
                          const newValues = formik.values.categoryIds.filter(
                            (id) => id !== option.category_id,
                          );
                          formik.setFieldValue("categoryIds", newValues);
                        }}
                      >
                        ✕
                      </span>
                    )}
                  </Box>
                </li>
              )}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select Categories"
                  name="categoryIds"
                  error={
                    formik.touched.categoryIds &&
                    Boolean(formik.errors.categoryIds)
                  }
                  helperText={
                    formik.touched.categoryIds &&
                    typeof formik.errors.categoryIds === "string"
                      ? formik.errors.categoryIds
                      : ""
                  }
                  fullWidth
                  size="small"
                  sx={{
                    // background: "#FFF",
                    ...autocompleteDropdownStyles.inputSx,
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option: any, index: number) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Box
                      key={option.category_id}
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        backgroundColor: MecordColors.ActiveBackground,
                        fontFamily: "Poppins, sans-serif",
                        borderRadius: "3px",
                        padding: "2px 6px",
                        margin: "1px",
                        fontSize: "13px",
                      }}
                      {...tagProps}
                    >
                      {option.category_name}
                    </Box>
                  );
                })
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#ced4da",
                  },
                  "&:hover fieldset": {
                    borderColor: "#ced4da",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ced4da",
                  },
                },
              }}
            />
          </Col>

          <Col xs={12} md={6} className="mb-3">
            <Form.Label className="fw-semibold">
              Sub-category Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="subCategoryName"
              placeholder="Enter Sub Category name"
              value={formik.values.subCategoryName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                !!formik.errors.subCategoryName &&
                formik.touched.subCategoryName
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.subCategoryName}
            </Form.Control.Feedback>
          </Col>
        </Row>

        {/* Buttons */}
        <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
          <CancelButton
            redirectTo="/master/sub-category"
            disabled={isSubmitting}
          />
          <PrimarySubmitButton
            label={"Submit"}
            isLoading={isSubmitting}
            loadingLabel={"Submitting..."}
          />
        </div>
      </Form>
    </PageContainer>
  );
};

export default SubCategoryForm;
